import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, CircularProgress, FormControlLabel, FormGroup, Grid, IconButton, MenuItem, Popover, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { useGetOne, useRecordContext } from "ra-core";
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import * as Yup from 'yup';
import { RuleDisplayRoot, useLmRuleDisplayDeltaMsg } from "./RuleDisplayComponents";
import { ClassificationRulesDisplayDto, ProtectionRuleDto, ProtectionRuleInput } from "@/gen/api/securefile";
import { LocalMessageLocation } from "@/contexts/LocalMessagingRoot";
import { RuleDisplayPresetContextProvider, RuleDisplayTree } from "./RuleDisplayPrimitives";
import { SecureFilePreset } from "./ruleDisplayPresets/securefilePreset";
import { useLmStatesPropagate } from "@/contexts/LocalMessageHookProtocols/useLmStatesPropagate";
import { promiseWithResolvers } from "@/utils/promiseWithResolvers";
import { useLmGlobalDisabledMsg } from "@/contexts/LocalMessageHookProtocols/useLmGlobalDisabledMsg";


const validationSchema =
  Yup.object().shape({
    displayName: Yup.string().max(200, 'Rule Name must be at most 200 characters').required('Rule Name is required'),
    comment: Yup.string().max(800, 'Description must be at most 800 characters'),    
  })

interface FormData {
  displayName: string;
  comment: string;
}

const editorDirty = (v: ReturnType<typeof protectionRuleEditStatesInit>) => {
  return Object.keys(v.touched).length > 0 || v.composedRuleDirty
}

export const DataProtectionRuleEdit: FC = () => {
  const record = useRecordContext()
  const dto: ProtectionRuleDto|undefined = record as any

  const [states, setStates] = useState(protectionRuleEditStatesInit(dto))
  useEffect(() => {
    setStates(protectionRuleEditStatesInit(dto))
  }, [dto])

  const {informDirty} = useLmStatesPropagate({
    key: 'editor',
    asUpperStage: {
      dirtyStateInformed: {
        handler(informDirty) {
          return (_, dirty) => {
            setStates(v => {
              v.composedRuleDirty = dirty
              informDirty(editorDirty(v))
              return v
            })
          }
        },
        deps: []
      }
    },
    asLowerStage: {
      collectStates: {
        handler(validateComposedRule) {
          return async () => {
            const [_, {composedRule}] = await Promise.all([
              validateForm().then(pass => pass ? Promise.resolve(pass) : Promise.reject()),
              validateComposedRule(['composedRule'])
            ])
            const qStates = promiseWithResolvers()
            setStates(v => {
              qStates.resolve(v)
              return v
            })
            const v1: typeof states = (await qStates.promise) as any
            
            const payload: ProtectionRuleInput = {
              displayName: v1.stage.displayName,
              comment: v1.stage.comment,
              activeRule: v1.stage.activeRule,
              ruleDisplayTree: JSON.stringify(composedRule)
                // ruleDefDirty ? JSON.stringify(composedRule) 
                // : dto?.ruleDisplayTree as any, // if rule def is not changed, this might save a rule set version bump
            }
            return payload
          }
        },
        deps: []
      }
    }
  })
  
  useLmGlobalDisabledMsg({
    listener(disabled) {
      setStates(v => 
        v.globalDisabled !== disabled ? {...v, globalDisabled: disabled} : v
      )
    }
  })
  
  const validate = (x: any) => {
    try {
      validationSchema.validateSync(x, {abortEarly:false})
    } catch (e: any) {
      if (e.name === 'ValidationError') {
        const errors = {} as Record<string, string>
        for (const err of e.inner) {
          errors[err.path] = err.message
        }
        return errors
      }
    }
  }
  const validateForm = () => {
    return new Promise<boolean>(resolve => {
      setStates(v => {
        v.errors = validate(v.stage) ?? {}
        v.touched = Object.keys(v.prevValue).reduce((acc, k) => {
          acc[k] = true
          return acc
        }, {} as any)
        resolve(Object.values(v.errors).every(x => !x))
        return {...v}
      })
    })
  }
  const timerRef = useRef({} as any)
  const fieldProps = (path: keyof FormData, transform?: (x:any)=>any) => ({
    value: states.stage[path],
    onChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (timerRef.current[path]) {
        window.clearTimeout(timerRef.current[path])
        delete timerRef.current[path]
      }
      const value = transform ? transform(e.target.value) : e.target.value;
      const setValue = (newValue: any) => {
        setStates(v => {
          (v.stage as any)[path] = newValue
          if (v.prevValue[path] !== newValue) {
            v.touched[path] = true
          } else {
            delete v.touched[path]
          }
          v.errors = validate(v.stage) ?? {}
          informDirty(editorDirty(v))
          return {...v}
        })
      }
      setValue(value)
      if (typeof value === 'string' && value.trim() !== value) {
        timerRef.current[path] = setTimeout(() => setValue(value.trim()), 1500)
      }
    },
    error: Boolean(states.touched[path] && states.errors[path]),
    helperText: states.touched[path] ? states.errors[path] : undefined,
  })
  const setActive = (a: boolean) => {
    setStates(v => {
      v.stage.activeRule = a
      if (v.prevValue.activeRule !== v.stage.activeRule) {
        v.touched.activeRule = true
      } else {
        delete v.touched.activeRule
      }
      informDirty(editorDirty(v))
      return {...v}
    })
  }
  const handleClose = () => {
    setStates(v => ({...v, copyClssRule: {...v.copyClssRule, 
      anchorEl: null,
      selectedCRule: '',
    }}))
  }
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => { 
    setStates(v => ({...v, copyClssRule: {...v.copyClssRule, anchorEl: e.target as any, shouldLoad: true }}))
  }

  const { data: clssRulesData, loading: clssRulesLoading } = useGetOne(states.copyClssRule.shouldLoad ? clssRuleRes : 'noop', 'edit')
  const clssRuleLs = useMemo(() => {
    const clssRulesDto : ClassificationRulesDisplayDto|undefined = clssRulesData as any
    if (!clssRulesDto) { return [] }
    return (clssRulesDto.currRuleSetIds && clssRulesDto.currRuleSetIds.length > 0
        ? clssRulesDto.allRules!.filter(x => clssRulesDto.currRuleSetIds!.indexOf(x.ruleId!) > -1)
        : clssRulesDto.allRules!).sort((a, b) => a.displayName! < b.displayName! ? -1 : 1)
  }, [clssRulesData])

  const {sendDownDelta} = useLmRuleDisplayDeltaMsg({ key: 'editor' })
  const performCopyRuleDef = () => {
    if (!states.copyClssRule.selectedCRule) { return }
    const selectedCRule = clssRuleLs.find(r => r.ruleId === (states.copyClssRule.selectedCRule as any))
    if (!selectedCRule) { return }
    const ruleDef : RuleDisplayTree = JSON.parse(selectedCRule.ruleDisplayTree ?? '{}')
    sendDownDelta('composedRule', ruleDef)
    
    handleClose()
  }

  return (
    <>
      <Grid container sx={{ py: 2, }} spacing={2} >
        <Grid item xs={5}>
            <TextField label="Rule Name*" fullWidth 
              {...fieldProps('displayName')}
            />
        </Grid>
        <Grid item xs={7}>
          <TextField label="Description" multiline fullWidth 
            {...fieldProps('comment')}
          />
        </Grid>
        <Grid item xs={5}>
          <FormGroup>
            <FormControlLabel 
              control={<Switch color="success" checked={states.stage.activeRule} 
                onChange={(e) => setActive(e.target.checked)} />}
              label="Active"
            />
          </FormGroup>
        </Grid>
      </Grid>
      <Stack direction="row" alignItems="center">
        <Typography variant="subtitle2">Rule Definition</Typography>
        <Tooltip title="More action...">
          <IconButton id="more-action-btn" onClick={handleOpen} size="small" sx={{ml:2}} color="secondary"><MoreVertIcon /></IconButton>
        </Tooltip>
        <Popover id={'pop-trial-run'} anchorOrigin={{ vertical: 'top', horizontal: 'left', }} transformOrigin={{ vertical: 'top', horizontal: 'left'}}
          open={!!states.copyClssRule.anchorEl}
          anchorEl={states.copyClssRule.anchorEl}
          onClose={handleClose}
        >
          <Box m={2} width={420}>
            <TextField label="Copy rule definition from existing Classification Rule" select fullWidth sx={{mb: 2}} 
              InputProps={{
                startAdornment: clssRulesLoading ? <CircularProgress size={16} /> : undefined
              }}
              value={states.copyClssRule.selectedCRule}
              onChange={(e) => { setStates(v => ({...v, copyClssRule: {...v.copyClssRule, selectedCRule: e.target.value }})) }}
            >
              {clssRuleLs.map(cRule => (
                <MenuItem key={cRule.ruleId!} value={cRule.ruleId!}>
                  <Tooltip title={cRule.displayName!}>
                    <Typography variant="body2" maxWidth={600}>
                      {cRule.displayName}
                    </Typography>
                  </Tooltip>
                </MenuItem>
              ))}
            </TextField>
            <Button color="primary" variant="contained" fullWidth
              onClick={performCopyRuleDef}
            >
              Copy
            </Button>
          </Box>
        </Popover>
      </Stack>
      <RuleDisplayPresetContextProvider value={SecureFilePreset}>
        <LocalMessageLocation id="composedRule">
          <RuleDisplayRoot globalDisabled={states.globalDisabled} />
        </LocalMessageLocation>
      </RuleDisplayPresetContextProvider>
    </>
  )
}

const protectionRuleEditStatesInit = (dto: ProtectionRuleDto|undefined) => {
  const initialValues = {
    displayName: dto?.displayName ?? '',
    comment: dto?.comment ?? '',
    activeRule: dto?.activeRule ?? true,
  }
  return ({
    prevValue: initialValues,
    stage: {...initialValues},
    errors: {} as { [K in keyof(typeof initialValues)]+?: string|undefined },
    touched: {} as { [K in keyof(typeof initialValues)]+?: boolean|undefined },
    globalDisabled: false,
    composedRuleDirty: false,
    copyClssRule: {
      shouldLoad: false,
      anchorEl: null as Element|null,
      selectedCRule: '',
    },
  })
}

const clssRuleRes = 'settings/securefile/classification-rules'

